import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ORGANIZATION_TYPE_LABELS, OrganizationType, getOrganizationTypeLabel } from 'src/app/core/models/organization.model';
import { OrganizationMembership, OrganizationMembershipCardComponent } from '../organization-membership-card.component';

/**
 * Sub-shape carried inside the org-detail admin payload. Hub joins
 * adn_organization_payment_accounts and returns this nested object so the
 * Overview tab can show Stripe identity status WITHOUT reading the
 * deprecated organization.stripeCustomerId / paymentMethodId columns.
 * See billing-payments § 2.1.
 */
interface OrgPaymentAccountsView {
  customer: {
    stripeCustomerId: string;
    status: string;
    defaultPaymentMethodId: string | null;
    subscriptionId: string | null;
    subscriptionStatus: string | null;
    subscriptionCurrentPeriodEnd: string | null;
  } | null;
  connect: {
    stripeAccountId: string;
    status: string;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
  } | null;
}

interface Organization {
  id: string;
  name: string;
  organizationType: OrganizationType;
  ownerProfileId: string;
  chiefSupervisorProfileId?: string;
  streetAddressLine1: string;
  streetAddressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  email?: string;
  phone?: string;
  ein?: string;
  parentOrganizationId?: string;
  billingResponsibility: string;
  /**
   * @deprecated 2026-05-21 — read from `paymentAccounts.customer.stripeCustomerId`
   * instead. Column on `adn_organizations` is no longer written by the new
   * payment flow; kept here only so this interface still type-checks against
   * pre-migration API responses during the deprecation window.
   */
  stripeCustomerId?: string;
  /** Live Stripe identity data from the new payment-accounts table (§ 2.1). */
  paymentAccounts?: OrgPaymentAccountsView;
  /** Core/Pro summary (ADN-2513); null when Hub could not read it from Stripe. */
  membership?: OrganizationMembership | null;
  zohoCompanyId?: string;
  createdAt: string;
  updatedAt: string;
}

interface OrganizationMember {
  id: string;
  profileId: string;
  role: string;
  firstName: string;
  lastName: string;
  email: string;
  joinedAt: string;
}

interface OrganizationStats {
  totalMembers: number;
  activeMembers: number;
  inactiveMembers: number;
  totalDrones: number;
  activeDrones: number;
  inactiveDrones: number;
  totalCertifications: number;
  validCertifications: number;
  expiringSoonCertifications: number;
  lastActivityAt: string | null;
}

@Component({
  selector: 'app-organization-overview-tab',
  standalone: true,
  imports: [CommonModule, OrganizationMembershipCardComponent],
  templateUrl: './organization-overview-tab.component.html',
  styleUrl: './organization-overview-tab.component.scss'
})
export class OrganizationOverviewTabComponent {
  @Input() organization: Organization | null = null;
  @Input() ownerProfile: OrganizationMember | null = null;
  @Input() chiefSupervisorProfile: OrganizationMember | null = null;
  @Input() stats: OrganizationStats | null = null;
  /** Bubbles a grant/revoke confirmation message up so the page can reload. */
  @Output() membershipChanged = new EventEmitter<string>();

  organizationTypes = ORGANIZATION_TYPE_LABELS;

  getOrganizationTypeLabel = getOrganizationTypeLabel;

  formatTimeAgo(dateString: string | null): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  maskEIN(ein: string | undefined): string {
    if (!ein) return 'Not provided';
    // Mask EIN: XX-XXX1234
    if (ein.length >= 4) {
      return `XX-XXX${ein.slice(-4)}`;
    }
    return 'XX-XXXXXXX';
  }
}
